import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { RotateCcw } from "lucide-react";
import { textModels, imageModels } from "@/lib/aiproviders/models";
import { ReactNode } from "react";

interface ModelSelectionDropdownProps {
  currentModel?: string;
  onSelect: (model: string) => void;
  className?: string;
  onOpenChange?: (open: boolean) => void;
  isImageMode?: boolean;
  trigger?: ReactNode;
  tooltipContent?: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
}

export function ModelSelectionDropdown({
  currentModel,
  onSelect,
  className,
  onOpenChange,
  isImageMode = false,
  trigger,
  tooltipContent,
  side = "bottom",
  align = "start",
}: ModelSelectionDropdownProps) {
  const models = isImageMode ? imageModels : textModels;

  const defaultTrigger = (
    <Button
      variant="ghost"
      size="icon"
      className={className}
      onFocus={(e) => {
        // Prevent tooltip from showing on focus
        e.preventDefault();
        e.currentTarget.blur();
      }}
    >
      <RotateCcw className="h-3 w-3" />
    </Button>
  );

  const triggerElement = trigger || defaultTrigger;

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      {tooltipContent ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>{triggerElement}</DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side={side}>{tooltipContent}</TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenuTrigger asChild>{triggerElement}</DropdownMenuTrigger>
      )}
      <DropdownMenuContent align={align} side={side} sideOffset={5} className="w-48">
        <DropdownMenuRadioGroup value={currentModel} onValueChange={onSelect}>
          {models.map((model) => (
            <DropdownMenuRadioItem key={model.id} value={model.id}>
              <span className="flex items-center gap-2">
                <span>{model.name}</span>
                {model.isAdvanced && (
                  <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                    PRO
                  </span>
                )}
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
